import {isString, isUndefined} from 'lodash';
import {parse} from 'qs';

import {
  addQueryToPath,
  isAbsoluteUrl,
  tryParseUrl,
} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {parseMailto} from '../../../srcCommon/helpers/mailtoHelpers';
import {logger} from '../../../srcCommon/services/logger/logger';
import {appRoutesPrefixes} from '../../components/appRoutesPrefixes';
import {tryParseCalendarNotificationLink} from '../../helpers/calendarReminderHelpers';
import {buildPublicId} from '../../models/applications/publicIdTypes';
import {fetchAsync} from '../../services/http/http';
import {HttpResponseError} from '../../services/http/httpError';
import {
  InterfaceEventsEnum,
  InviteTeammatePopoverAccessPointsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {AppState} from '../appState';
import {Dispatch, StateGetter} from '../appStore';
import {ThunkAction, ThunkAsyncAction} from '../appThunk';
import {getFindChannelIdFromChannelAddress} from '../data/channels/channelsSelectors';
import {getCompany, getCompanyHost} from '../data/company/companySelectors';
import {getHost} from '../data/dataSelectors';
import {composeMessageAction} from '../data/messages/draftMessagesActions';
import {getUser, getUserNamespace} from '../data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf, getFlatTeams} from '../data/teams/teamsSelectors';
import {openInviteTeammatePopoverAction} from '../panels/app/appPanelActions';
import {NavigationBlockerId} from '../panels/app/appPanelHelpers';
import {pushPathAndFocusAction, tryParsePath} from '../panels/app/appPanelRouteActions';
import {AppContentTypesEnum} from '../panels/app/parameters/appParameters';
import {openCalendarEventAction} from '../panels/calendars/calendarsPanelActions';
import {CellsContext} from '../panels/cells/context/cellsContext';
import {InboxesParametersTypesEnum} from '../panels/inboxes/parameters/inboxesParameters';
import {
  findOnlySelectedId,
  inboxesParametersSelectAllKeyword,
} from '../panels/inboxes/parameters/inboxesParametersHelpers';
import {getFirstAppPanelId} from '../panels/panelsSelectors';
import {NewPanelId} from '../panels/panelStateBase';
import {popoutViewerAction} from '../panels/viewer/viewerPanelActions';

/*
 * Constants & Types.
 */

export const allowedProtocols = ['http:', 'https:', 'mailto:'];
export const helpCenterUrl = 'https://help.frontapp.com';
export const blogUrl = 'https://frontapp.com/blog';

/*
 * Errors.
 */

export class NavigationInsecureError extends CustomError {
  constructor() {
    super('[navigation] Cannot navigate to insecure link.');
  }
}

export class NavigationForbiddenError extends CustomError {
  constructor() {
    super('[navigation] Access to resource unauthorized.');
  }
}

export class NavigationNotFoundError extends CustomError {
  constructor() {
    super('[navigation] Resource not found.');
  }
}

export class NavigationError extends CustomError {
  constructor() {
    super('[navigation] An error occurred.');
  }
}

/*
 * Action creators.
 */

export function navigateToConversation(conversationId: number): ThunkAction {
  return (dispatch) => {
    const conversationPublicId = buildPublicId('conversation', conversationId);

    dispatch(navigateToLinkAction(conversationPublicId));
  };
}

// Note: the `preferenceKey` correspond to the key of the preferences that can be found in
// server/resources/teammate/preferences.schema.json in the BE code.
export function navigateToSpecificPreferenceAction(preferenceKey: string): ThunkAction {
  return (dispatch, getState) => {
    const userNamespace = getUserNamespace(getState());
    const path = addQueryToPath(`/settings/${userNamespace}/user-preferences`, {
      search: preferenceKey,
    });

    return dispatch(navigateToLinkAction(path));
  };
}

export function navigateToSnoozesAction(): ThunkAction {
  return (dispatch, getState) => {
    const userNamespace = getUserNamespace(getState());
    return dispatch(navigateToLinkAction(`/settings/${userNamespace}/snoozes`));
  };
}

export function navigateToLinkAction(
  link: string,
  shiftKey?: boolean,
  ignoreNavigationBlockerId?: NavigationBlockerId,
): ThunkAsyncAction<true> {
  return async (dispatch, getState) => {
    const host = getCompanyHost(getState());
    const url = tryParseUrl(link, host);
    if (await navigateToInternalLinkAsync(dispatch, getState, link, url, ignoreNavigationBlockerId)) {
      return true;
    }

    if (await navigateToFrontIdAsync(dispatch, getState, link, url, shiftKey)) {
      return true;
    }

    if (navigateToComposer(dispatch, getState, link)) {
      return true;
    }

    if (navigateToExternalLink(link, url)) {
      return true;
    }

    throw new NavigationError();
  };
}

/*
 * Navigation logic.
 */

/** Navigate to any internal Front link. */
async function navigateToInternalLinkAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  link: string,
  url?: URL,
  ignoreNavigationBlockerId?: NavigationBlockerId,
) {
  const appPanelId = getFirstAppPanelId(getState());
  // Check if this is a go link.
  if (await navigateToGoLinkAsync(dispatch, getState, url)) {
    return true;
  }

  // Check if this is a calendar notification link to open a calendar event.
  if (navigateToCalendarEventLink(dispatch, link)) {
    return true;
  }

  // If this is a relative URL, navigate directly.
  if (!isAbsoluteUrl(link) && link.startsWith('/') && !link.startsWith('/open/')) {
    dispatch(pushPathAndFocusAction(appPanelId, link, ignoreNavigationBlockerId));
    return true;
  }

  // If the link is an absolute url that is also an internal link, navigate to the path.
  if (
    isAbsoluteUrl(link) &&
    url &&
    isInternalLink(getState(), url) &&
    appRoutesPrefixes.some((prefix) => url.pathname.startsWith(`/${prefix}`)) &&
    !url.pathname.startsWith('/visitors/') // Force visitor links to open in a new tab.
  ) {
    dispatch(pushPathAndFocusAction(appPanelId, url.pathname, ignoreNavigationBlockerId));
    return true;
  }

  // Otherwise, this is not an internal link.
  return false;
}

/** Navigate to a link like https://app.frontapp.com/go?link=... */
async function navigateToGoLinkAsync(dispatch: Dispatch, getState: StateGetter, url?: URL) {
  // If this is not a "go" URL, nothing to do.
  if (!url || url.pathname !== '/go') {
    return false;
  }

  // Parse the query string to find the "link" argument.
  const query = parse(url.search, {ignoreQueryPrefix: true});
  if (!query || !query.link || !isString(query.link)) {
    return false;
  }

  // If a link was found, go back to square one.
  return dispatch(navigateToLinkAction(query.link));
}

/** Navigate to a link like /open-calendar-event/abcdef */
function navigateToCalendarEventLink(dispatch: Dispatch, link: string) {
  const parameters = tryParseCalendarNotificationLink(link);
  if (!parameters) {
    return false;
  }

  dispatch(openCalendarEventAction(parameters.focusedDate, parameters.calendarEventParameters));
  trackInterfaceEvent(InterfaceEventsEnum.CLICK_CALENDAR_NOTIFICATION);
  return true;
}

/** Navigate to a link like 123213 (internal conversation ID), cnv_123 or a transactional link. */
export async function navigateToFrontIdAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  link: string,
  url?: URL,
  shiftKey?: boolean,
) {
  const state = getState();
  const company = getCompany(state);

  // Make sure that this is either a Front ID or a Front link.
  const isFrontId = /^\d+$/.test(link) || /^[a-z]{3}_\w+$/.test(link);
  const isFrontLink = url && isInternalLink(state, url) && url.pathname.startsWith('/open/');
  if (!isFrontId && !isFrontLink) {
    return false;
  }

  try {
    // Resolve the link through the server.
    const serverUrl = addQueryToPath(`${company.url}/app_link`, {link});
    const response = await fetchAsync(serverUrl);
    const navigationLink = response.app_link;
    if (!isString(navigationLink)) {
      throw new NavigationError();
    }

    // If shift is pressed, try to open a popup.
    if (shiftKey && dispatch(navigateInPopupAction(navigationLink))) {
      return true;
    }

    // TODO: Take a panel ID as an argument.
    const panelId = getFirstAppPanelId(getState());

    // Otherwise, navigate the main window.
    dispatch(pushPathAndFocusAction(panelId, navigationLink));
    return true;
  } catch (error) {
    if (!(error instanceof HttpResponseError)) {
      throw error;
    }

    switch (error.status) {
      case 404:
        throw new NavigationNotFoundError();

      case 403:
        throw new NavigationForbiddenError();

      default:
        throw new NavigationError();
    }
  }
}

function navigateInPopupAction(path: string): ThunkAction<boolean> {
  return (dispatch, getState) => {
    // Parse the provided link.
    const appParameters = tryParsePath(getState(), path);
    if (!appParameters || appParameters.contentType !== AppContentTypesEnum.INBOXES) {
      return false;
    }

    // We only support popup navigation to conversations.
    const {parameters} = appParameters;
    if (!parameters || parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
      return false;
    }

    // Make sure we have a single conversation ID.
    const selectedId = findOnlySelectedId(parameters.selection);
    if (isUndefined(selectedId)) {
      return false;
    }

    // If so, open a viewer for these parameters.
    dispatch(popoutViewerAction(parameters));
    return true;
  };
}

/** Navigate to a link like https://frontapp.com. */
function navigateToExternalLink(link: string, url?: URL) {
  if (!url) {
    return false;
  }

  if (!allowedProtocols.includes(url.protocol)) {
    throw new NavigationInsecureError();
  }

  if (link !== String(url)) {
    logger.warn('External link URL discrepancy', {link, url: String(url)});
  }

  if (!link.toLowerCase().startsWith(url.protocol)) {
    throw new NavigationInsecureError();
  }

  // TODO: Use `String(url)` instead of `link`, based on the logging above.
  openInBrowser(link);
  return true;
}

export function navigateToComposer(dispatch: Dispatch, getState: StateGetter, link: string) {
  // Parsing mailto URL.
  const options = parseMailto(link);
  if (!options) {
    return false;
  }

  // Try to find a channel ID from the provided "from", if any.
  const channelId = getFindChannelIdFromChannelAddress(getState())(options.from);

  // Starting composer from parsed options.
  try {
    dispatch(
      composeMessageAction({
        trustedHtml: options.trustedHtml,
        text: options.text,
        recipients: options.recipients,
        subject: options.subject,
        channelId,
      }),
    );

    return true;
  } catch (error) {
    // composeMessageAction can throw an error if no compose channel can be found.
    return false;
  }
}

/**
 * If there's only one team, open the teammate invite popover.
 * Otherwise, navigate to the settings add teammate path.
 */
export function navigateToAddTeammateAction(
  panelId: NewPanelId,
  addTeammateSettingsPath: string,
  accessPoint: InviteTeammatePopoverAccessPointsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const teams = getFlatTeams(state);

    if (teams.length === 1) {
      return dispatch(openInviteTeammatePopoverAction(panelId, accessPoint));
    }

    return dispatch(navigateToLinkAction(addTeammateSettingsPath));
  };
}

/**
 * Navigates to "Message Templates" in the settings, preferably for the first team that the user is an admin of.
 */
export function navigateToMessageTemplatesAction(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const user = getUser(getState());
    const firstTeamUserIsAdminOf = getFirstTeamUserIsAdminOf(state);

    const messageTemplatesNamespace = firstTeamUserIsAdminOf
      ? firstTeamUserIsAdminOf.namespace
      : user.namespace;

    return dispatch(navigateToLinkAction(`/settings/${messageTemplatesNamespace}/answers`));
  };
}

/**
 * Navigates to the Analytics app.
 */
export function navigateToAnalyticsLinkAction(path: string): ThunkAction {
  return (dispatch) => dispatch(navigateToLinkAction(path));
}

export function selectAllConversationsAction(cellsContext: CellsContext) {
  return navigateToLinkAction(`${cellsContext.path}/${inboxesParametersSelectAllKeyword}`);
}

export function clearSelectedConversationsAction(cellsContext: CellsContext) {
  return navigateToLinkAction(cellsContext.path);
}

/*
 * Helpers.
 */

function isInternalLink(state: AppState, url?: URL | undefined) {
  if (!url) {
    return false;
  }

  // Compare the URL to the known hosts.
  const host = getHost(state);
  const companyHost = getCompanyHost(state);

  return url.origin === location.origin || url.origin === host || url.origin === companyHost;
}
